import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { cn } from '@/utils';
import React, { PropsWithChildren, useState, useRef } from 'react';
interface TooltipProps {
  message: string | React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  content?: TooltipContentProps;
}

const Tooltip = ({
  children,
  message,
  open,
  onOpenChange,
  content,
}: PropsWithChildren<TooltipProps>) => {
  const [isHovered, setIsHovered] = useState(false);
  const isOpen = open !== undefined ? open : isHovered;

  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Add a delay before hiding the tooltip
    hoverTimeout.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root open={isOpen} onOpenChange={onOpenChange}>
        <TooltipPrimitive.Trigger
          asChild
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Content
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            'radix-side-top:animate-slide-down-fade',
            'radix-side-right:animate-slide-left-fade',
            'radix-side-bottom:animate-slide-up-fade',
            'radix-side-left:animate-slide-right-fade radix',
            'inline-flex items-center rounded-md px-4 py-2.5',
            'z-50 bg-white text-base ring-1 ring-inset ring-brand-dark-50 dark:bg-brand-dark-75 dark:ring-brand-light-15',
          )}
          sideOffset={4}
          {...content}
        >
          <TooltipPrimitive.Arrow className="fill-current text-white dark:text-brand-light-15" />
          <span className="block text-xs leading-none text-brand-dark-100 dark:text-brand-25">
            {message}
          </span>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

export { Tooltip };
